import { ActivatedRouteSnapshot } from '@angular/router';
import { createSelector } from '@ngrx/store';
import { FullInvoiceFragment } from 'graphql.generated';
import { isNil, minBy } from 'lodash';
import { LoadingState } from 'src/app/utilities/state.util';

import { brandingFeature, createConfigSelector } from '../../../state/branding.store';
import { selectCurrentRoute } from '../../../state/router';
import { overviewLocationsFeature } from '../../job-overview/overview-locations/overview-locations.feature';
import { jobToolFeature, selectJob } from '../../job-tool.reducer';
import { jobCreateCustomerFeature } from '../job-create-customer/job-create-customer.feature';

export const selectIsJobCreationToolOpen = createSelector(
  selectCurrentRoute,
  (route: ActivatedRouteSnapshot) => {
    if (!route?.routeConfig) { return false; }
    return route.routeConfig.path === ':jobId/edit';
  }
);

export const selectCustomerInput = createSelector(
  jobCreateCustomerFeature.selectCustomerEditor,
  (customerInput) => customerInput
);

export const selectZoneIdForFind = createSelector(
  overviewLocationsFeature.selectServiceArea,
  (resolvedServiceArea) => resolvedServiceArea?.id
);

export const selectCustomerRole = createConfigSelector('ROLE_CUSTOMER')

// export const selectAuthorId = createSelector(
//   jobToolFeature.selectUser,
//   (user) => user?.id,
// )

export const selectLocationsForAddToJob = createSelector(
  overviewLocationsFeature.selectLocationEditors,
  (locationsInputs) => {
    if (!locationsInputs) {
      return [];
    }

    return Object.keys(locationsInputs)
      .map(key => {
        const location = locationsInputs[key]?.selectedLocation;
        return {
          locationId: location?.id,
          locationType: key
        };
      });
  }
);

export const selectSummariesForSaving = createSelector(
  selectJob,
  (jobInput) => {
    const crewSummary = jobInput?.crewSummary;
    const adminSummary = jobInput?.adminSummary;
    const customerSummary = jobInput?.customerSummary;

    const result: any = {};

    if (crewSummary) {
      result.crewSummary = crewSummary;
    }
    if (adminSummary) {
      result.adminSummary = adminSummary;
    }
    if (customerSummary) {
      result.customerSummary = customerSummary;
    }

    return result;
  }
);

export const selectMetadataForSaving = createSelector(
  selectJob,
  (jobInput) => {
    const metadata = jobInput?.metadata;

    if (!metadata) {
      return null;
    }

    const result: any = {};

    // Iterate over the keys in metadata and include only those with non-undefined values
    Object.keys(metadata).forEach((key) => {
      const value = metadata[key];
      if (value !== undefined) {
        result[key] = value;
      }
    });

    return result;
  }
);

export const selectTimelinesForSaving = createSelector(
  selectJob,
  (jobInput) => {
    const timeline = jobInput?.timeline;
    const timelineDays = jobInput?.timelineDays;

    const result: any = {};

    if (timeline) {
      result.timeline = timeline;
    }
    if (timelineDays) {
      result.timelineDays = timelineDays;
    }

    return result;
  }
);

export const selectJobCategory = createSelector(
  selectJob,
  (jobInput) => {
    return jobInput?.jobCategory?.toLowerCase();
  }
);

export const selectZoneIdForCreatingJob = createSelector(
  overviewLocationsFeature.selectServiceArea,
  (resolvedServiceArea) => {
    return resolvedServiceArea?.id;
  }
);

export const selectJobCreationVariables = createSelector(
  selectCustomerInput,
  brandingFeature.selectCurrency,
  // selectAuthorId,
  selectLocationsForAddToJob,
  selectSummariesForSaving,
  selectMetadataForSaving,
  selectTimelinesForSaving,
  selectJobCategory,
  selectZoneIdForCreatingJob,
  (customer, currency, locations, summaries, metadata, timelines, jobCategory, selectZoneIdForCreatingJob) => {
    return {
      stage: 'lead',
      currency,
      metadata,
      jobCategory,
      ...summaries,
      users: [
        // {
        //   role: JOB_ROLE_MAP.customerRole,
        //   userId: customer?.id,
        // },
        // Deprecated: The sales agent is now set in the backend.
        // {
        //   role: JOB_ROLE_MAP.salesAgentRole,
        //   userId: authorId,
        // }
      ],
      locations: [...locations],
      zoneId: selectZoneIdForCreatingJob,
      ...timelines
    };
  }
);

export const selectJobSavingErrors = createSelector(
  jobToolFeature.selectCallState,
  (callState) => {

    const errorKeys = ['createCustomer', 'createJob', 'setFields', 'setTags'];
    const errors: string[] = [];

    errorKeys.forEach(key => {
      if (callState && callState[key]?.error) {
        errors.push(callState[key].error);
      }
    });

    return errors;
  }
);

export const selectJobSavingInProgress = createSelector(
  jobToolFeature.selectCallState,
  (callState) => {
    const loadingKeys = ['createCustomer', 'updateCustomer', 'createJob', 'setFields', 'setTags', 'closeJob'];

    return loadingKeys.some(key => callState && callState[key] === LoadingState.LOADING);
  }
);

// export const selectCreateJobCallState = createSelector(
//   jobToolFeature.selectCallState,
//   (callState) => callState.createJob
// );

export const selectStartTimeCanBeEdited = createSelector(
  selectJob,
  (job) => {

    //this is simplified version of timeline section
    //if job already has moving event, we don't allow change it's time
    const movingEvent = job?.events?.filter((event) => event?.type === 'moving');

    return !movingEvent;
  }
);

export const selectLatestInvoiceId = createSelector(
  selectJob,
  (job): string | undefined => {
    const allInvoices = job?.events?.flatMap(event => event?.invoices) ?? [];
    const activeInvoices = allInvoices.filter(
      invoice => isNil(invoice?.voidedAt) && isNil(invoice?.deletedAt)
    );
    const earliestInvoice = minBy(activeInvoices, 'updatedAt') as FullInvoiceFragment;

    return earliestInvoice?.id ?? undefined;
  }
);

export const jobCreateSelectors = {
  selectZoneIdForFind,
  selectCustomerRole,
  selectSummariesForSaving,
  selectMetadataForSaving,
  selectJobSavingErrors,
  selectJobSavingInProgress,
  selectZoneIdForCreatingJob,
  // selectCreateJobCallState,
  selectStartTimeCanBeEdited,
  selectLatestInvoiceId,
};
